import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import { Briefcase, MapPin, Star, Calendar as CalendarIcon, Loader2, X, Eye, EyeOff } from "lucide-react";

const STATUS_COLORS = {
  scheduled: "bg-blue-100 text-blue-700 border-blue-200",
  in_progress: "bg-brand-100 text-brand-700 border-brand-100",
  completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const FILTERS = [
  { v: "all", l: "All" },
  { v: "scheduled", l: "Scheduled" },
  { v: "in_progress", l: "In Progress" },
  { v: "completed", l: "Completed" },
];

export default function AdminJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // job being scheduled

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/jobs`, { withCredentials: true });
      setJobs(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: jobs.length, scheduled: 0, in_progress: 0, completed: 0 };
    jobs.forEach((j) => { c[j.status] = (c[j.status] || 0) + 1; });
    return c;
  }, [jobs]);

  const filtered = filter === "all" ? jobs : jobs.filter((j) => j.status === filter);

  return (
    <DashboardShell>
      <div className="mb-8" data-testid="admin-jobs-page">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Admin</div>
        <h1 className="mt-2 text-4xl font-extrabold text-stone-900" style={{ fontFamily: "Outfit" }}>
          All jobs
        </h1>
        <p className="mt-2 text-stone-600">
          Every customer job across the business. Schedule, pin, and track from one place.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            data-testid={`jobs-filter-${f.v}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              filter === f.v
                ? "bg-stone-900 text-white shadow-md"
                : "bg-white text-stone-600 border border-stone-200 hover:border-stone-300"
            }`}
          >
            {f.l}
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${filter === f.v ? "bg-white/20" : "bg-stone-100"}`}>
              {counts[f.v] || 0}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-brand-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center bg-white rounded-2xl border border-dashed border-stone-300">
          <div className="w-16 h-16 mx-auto bg-brand-100 rounded-full flex items-center justify-center">
            <Briefcase className="w-8 h-8 text-brand-700" />
          </div>
          <h3 className="mt-6 text-2xl font-bold text-stone-900" style={{ fontFamily: "Outfit" }}>No jobs in "{filter}"</h3>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-jobs-grid">
          {filtered.map((j, i) => (
            <div key={j.id} data-testid={`admin-job-card-${i}`} className="group bg-white border border-stone-200 hover:border-stone-300 hover:shadow-md rounded-2xl p-5 transition-all">
              <div className="flex items-center justify-between mb-3">
                <span className={`text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border ${STATUS_COLORS[j.status] || STATUS_COLORS.scheduled}`}>
                  {(j.status || "scheduled").replace("_", " ")}
                </span>
                {j.rating ? (
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-3.5 h-3.5 ${n <= j.rating ? "text-brand-500 fill-brand-500" : "text-stone-300"}`} />
                    ))}
                  </div>
                ) : null}
              </div>

              <Link to={`/dashboard/jobs/${j.id}`} className="block">
                <h3 className="text-lg font-bold text-stone-900 line-clamp-2 hover:text-brand-700 transition-colors" style={{ fontFamily: "Outfit" }}>
                  {j.title}
                </h3>
              </Link>
              <div className="mt-2 text-sm text-stone-600 truncate">{j.customer_name || "—"}</div>
              <div className="mt-1 text-xs text-stone-500 truncate">{j.customer_email}</div>
              {j.address && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-stone-500">
                  <MapPin className="w-3 h-3" />
                  <span className="truncate">{j.address}</span>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between gap-2">
                {j.scheduled_date ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-700 bg-brand-50 border border-brand-100 rounded-full px-3 py-1">
                    <CalendarIcon className="w-3 h-3" />
                    {new Date(j.scheduled_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    {j.scheduled_time ? ` · ${j.scheduled_time}` : ""}
                  </span>
                ) : (
                  <span className="text-xs text-stone-400">Not scheduled</span>
                )}
                <button
                  onClick={() => setEditing(j)}
                  data-testid={`schedule-job-${i}`}
                  className="text-xs font-bold text-stone-700 hover:text-brand-700 px-3 py-1.5 rounded-full bg-stone-50 hover:bg-brand-50 border border-stone-200 hover:border-brand-200 transition-all"
                >
                  {j.scheduled_date ? "Reschedule" : "Pin date"}
                </button>
              </div>

              <div className="mt-2">
                <ShowcaseToggle
                  job={j}
                  onUpdated={(u) => setJobs((prev) => prev.map((x) => (x.id === u.id ? { ...x, ...u } : x)))}
                  testId={`showcase-toggle-${i}`}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ScheduleModal
          job={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setJobs((prev) => prev.map((j) => (j.id === updated.id ? { ...j, ...updated } : j)));
            setEditing(null);
          }}
        />
      )}
    </DashboardShell>
  );
}

function ScheduleModal({ job, onClose, onSaved }) {
  const [date, setDate] = useState(job.scheduled_date || "");
  const [time, setTime] = useState(job.scheduled_time || "");
  const [notes, setNotes] = useState(job.admin_notes || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/jobs/${job.id}/schedule`,
        { scheduled_date: date || null, scheduled_time: time || null, admin_notes: notes },
        { withCredentials: true }
      );
      toast.success("Schedule updated");
      onSaved(data);
    } catch (e) {
      toast.error("Failed to schedule");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/jobs/${job.id}/schedule`,
        { scheduled_date: null, scheduled_time: null },
        { withCredentials: true }
      );
      toast.success("Removed from calendar");
      onSaved(data);
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm" onClick={onClose}>
      <div
        data-testid="schedule-modal"
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-700">Pin to calendar</div>
            <h3 className="mt-1 text-xl font-bold text-stone-900" style={{ fontFamily: "Outfit" }}>{job.title}</h3>
            <div className="text-sm text-stone-500">{job.customer_name}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-stone-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-stone-600 mb-2 block">Date</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="schedule-date-input"
              className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-stone-600 mb-2 block">Time (optional)</span>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              data-testid="schedule-time-input"
              className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-stone-600 mb-2 block">Internal notes (optional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
              placeholder="Bring the surface cleaner; gate code 1234"
            />
          </label>
        </div>
        <div className="mt-6 flex flex-wrap gap-3 justify-end">
          {job.scheduled_date && (
            <button onClick={clear} disabled={busy} className="px-5 py-2.5 rounded-full text-sm font-bold bg-stone-100 hover:bg-stone-200 text-stone-700">
              Clear
            </button>
          )}
          <button
            onClick={save}
            disabled={busy || !date}
            data-testid="schedule-save-btn"
            className="px-6 py-2.5 rounded-full text-sm font-bold bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white"
          >
            {busy ? "Saving..." : "Pin to calendar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShowcaseToggle({ job, onUpdated, testId }) {
  const [busy, setBusy] = useState(false);
  const featured = !!job.showcase;
  const canFeature = (job.after_photos || []).length > 0;

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/jobs/${job.id}`,
        { showcase: !featured },
        { withCredentials: true }
      );
      onUpdated(data);
      toast.success(!featured ? "Featured on home page" : "Removed from home page");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  if (!canFeature) {
    return (
      <div className="text-[10px] text-stone-400 italic">
        Add an after photo to feature on home page
      </div>
    );
  }

  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all w-full justify-center ${
        featured
          ? "bg-brand-600 text-white border-brand-700 hover:bg-brand-700"
          : "bg-white text-stone-700 border-stone-200 hover:border-brand-200 hover:text-brand-700"
      }`}
    >
      {featured ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
      {featured ? "Featured on home page" : "Feature on home page"}
    </button>
  );
}

